import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { ProposalParams } from "work/proposals/_schemas/proposal-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/**
 * What one row of a draft collection is, against the draft as last saved.
 *
 * - `unchanged`: the saved row, as saved.
 * - `added`: a row the saved draft does not have.
 * - `edited`: the saved row, with other values (a fee schedule category only).
 * - `removed`: a saved row the draft no longer has.
 */
export type DraftRowChange = "unchanged" | "added" | "edited" | "removed";

type AnyFeeScheduleCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

/**
 * The saved rows that `isPresent` does not find in `current`, in their saved order.
 *
 * A draft does not draw these from its spec, because they are not in it. They are drawn struck
 * through beside it until the next save, so that each one has a place for its Undo (#6995).
 */
export function savedRowsMissingFrom<T>(
  saved: readonly T[],
  current: readonly T[],
  isPresent: (savedRow: T, current: readonly T[]) => boolean,
): T[] {
  return saved.filter((savedRow) => !isPresent(savedRow, current));
}

/**
 * `current` with one saved row put back.
 *
 * A row that `sameRow` finds in `current` is replaced in place: that is the Undo of an edit. Any
 * other row goes back after the nearest row before it in the saved order that `current` still has,
 * or first when there is none: that is the Undo of a removal.
 */
export function withSavedRowRestored<T>(
  saved: readonly T[],
  current: readonly T[],
  restored: T,
  sameRow: (a: T, b: T) => boolean,
): T[] {
  const currentIndex = current.findIndex((row) => sameRow(row, restored));
  if (currentIndex >= 0) return current.map((row, index) => (index === currentIndex ? restored : row));

  const savedIndex = saved.findIndex((row) => sameRow(row, restored));
  const result = [...current];
  for (let index = savedIndex - 1; index >= 0; index--) {
    const anchor = result.findIndex((row) => sameRow(row, saved[index]));
    if (anchor < 0) continue;
    result.splice(anchor + 1, 0, restored);
    return result;
  }
  return [restored, ...result];
}

/**
 * A saved draft's collections: the rows each one held when last saved, and what their Undo puts back
 * (#6995). The collection counterpart of `SavedDraftFields`.
 *
 * Each restore writes through the collection's own builder setter, the one its Remove and its edits
 * use, so an Undo reaches the save as the same operations any other change to the row would.
 *
 * The client team is not here. A saved draft stages its client team on the negotiation, not in the
 * builder, so `ProposalClientTeamView` undoes a removal there with {@link withSavedRowRestored}.
 *
 * The draft page builds one only once the draft has been saved. A draft that has never been saved has
 * no saved row to return to, so it has none, and shows no Undo (#6873).
 */
export default class SavedDraftCollections {
  public constructor(private readonly saved: ProposalParams) {}

  // #region Fee schedule
  /** What `category` is against the saved schedule. Never `removed`: a removed row is not current. */
  public feeScheduleCategoryChange(category: AnyFeeScheduleCategory): DraftRowChange {
    const saved = this.savedFeeSchedule().find((savedCategory) => savedCategory.id.isEqualTo(category.id));
    if (!saved) return "added";
    return saved.isEqualTo(category) ? "unchanged" : "edited";
  }

  /**
   * The saved categories `current` no longer has.
   *
   * A category counts as gone only when neither its id nor its name is in the schedule. The tab
   * matches categories by name, and one put back from a template carries a new id: putting the saved
   * row back beside it would give the schedule two categories of that name.
   */
  public removedFeeScheduleCategories(current: ProposalParams): AnyFeeScheduleCategory[] {
    return savedRowsMissingFrom(this.savedFeeSchedule(), feeScheduleOf(current), (savedCategory, categories) =>
      categories.some(
        (category) => category.id.isEqualTo(savedCategory.id) || category.name?.isEqualTo(savedCategory.name),
      ),
    );
  }

  /** The category with `categoryId` as last saved, or undefined when the saved draft has none. */
  public savedFeeScheduleCategory(categoryId: Guid): AnyFeeScheduleCategory | undefined {
    return this.savedFeeSchedule().find((category) => category.id.isEqualTo(categoryId));
  }

  /**
   * The other category in `current` that already holds the saved name of `categoryId`, if any.
   *
   * Undo on a renamed category puts its saved name back. When the draft has since given that name to
   * another category, the Undo would leave two categories of one name, and the tab's edits and removals
   * match categories by name, so they would then act on both. Such an Undo is refused until the other
   * category is renamed or removed; neither category is overwritten.
   */
  public feeScheduleCategoryNameConflict(categoryId: Guid, current: ProposalParams): AnyFeeScheduleCategory | undefined {
    const saved = this.savedFeeScheduleCategory(categoryId);
    if (!saved) return undefined;
    return feeScheduleOf(current).find(
      (category) => !category.id.isEqualTo(categoryId) && category.name?.isEqualTo(saved.name),
    );
  }

  /**
   * Puts the saved category back: in place of its edit, or in its saved position after a removal.
   *
   * `restored` is the saved category as the caller settled it, when the caller had to change it: the
   * fee schedule tab hands back the saved rate with its team member reconciled to the current vendor
   * team. Its id is the saved one. Refused, leaving `builder` as it is, while another category holds
   * the saved name (see {@link feeScheduleCategoryNameConflict}).
   */
  public restoreFeeScheduleCategory(
    categoryId: Guid,
    builder: ProposalBuilder,
    restored?: AnyFeeScheduleCategory,
  ): ProposalBuilder {
    const savedCategories = this.savedFeeSchedule();
    const saved = this.savedFeeScheduleCategory(categoryId);
    if (!saved || this.feeScheduleCategoryNameConflict(categoryId, builder.currentSpec)) return builder;

    const categories = withSavedRowRestored(savedCategories, feeScheduleOf(builder.currentSpec), restored ?? saved, sameId);
    return builder.setFeeSchedule(categories as FeeScheduleCategory[]);
  }
  // #endregion

  // #region Vendor team
  /**
   * The saved vendor team members `current` no longer has. A saved member who now leads the team is
   * still on it, so is not listed: putting them back as a member would list the leader twice.
   */
  public removedVendorTeamMembers(current: ProposalParams): EntityMember[] {
    const currentTeam = vendorTeamOf(current);
    return savedRowsMissingFrom(this.savedVendorTeamMembers(), currentTeam?.members ?? [], (savedMember, members) =>
      Boolean(currentTeam?.isLedBy(savedMember)) || members.some((member) => member.isSamePersonAs(savedMember)),
    );
  }

  /**
   * Puts a saved member back on the vendor team, in their saved position.
   *
   * Only the member comes back. A fee schedule rate that named them, and that the removal left with
   * no team member, stays as it is (#6995).
   */
  public restoreVendorTeamMember(userId: Guid, builder: ProposalBuilder): ProposalBuilder {
    const savedMembers = this.savedVendorTeamMembers();
    const restored = savedMembers.find((member) => member.userId.isEqualTo(userId));
    const currentTeam = vendorTeamOf(builder.currentSpec);
    if (!restored || currentTeam?.isLedBy(restored)) return builder;

    const members = withSavedRowRestored(savedMembers, currentTeam?.members ?? [], restored, samePerson);
    const team = (currentTeam ?? new VendorTeam({ members: [] })).clone({ members });
    return builder.setVendorTeam(team.withLeaderRemovedFromMembers());
  }
  // #endregion

  // #region Documents
  /** The saved documents of `type` that `current` no longer has. */
  public removedDocuments(type: WorkDocumentType, current: ProposalParams): WorkDocument[] {
    return savedRowsMissingFrom(this.savedDocuments(type), documentsOf(current, type), (savedDocument, documents) =>
      documents.some((document) => document.id.isEqualTo(savedDocument.id)),
    );
  }

  /** Puts a saved document of `type` back, in its saved position. */
  public restoreDocument(type: WorkDocumentType, documentId: Guid, builder: ProposalBuilder): ProposalBuilder {
    const savedDocuments = this.savedDocuments(type);
    const restored = savedDocuments.find((document) => document.id.isEqualTo(documentId));
    if (!restored) return builder;

    const documents = withSavedRowRestored(savedDocuments, documentsOf(builder.currentSpec, type), restored, sameId);
    if (type === WorkDocumentType.Conflicts) return builder.setConflicts(documents);
    if (type === WorkDocumentType.VendorPolicy) return builder.setVendorPolicies(documents);
    return builder.setClientPolicies(documents);
  }
  // #endregion

  private savedFeeSchedule(): AnyFeeScheduleCategory[] {
    return feeScheduleOf(this.saved);
  }

  private savedVendorTeamMembers(): EntityMember[] {
    const savedTeam = vendorTeamOf(this.saved);
    return savedTeam?.members.filter((member) => !savedTeam.isLedBy(member)) ?? [];
  }

  private savedDocuments(type: WorkDocumentType): WorkDocument[] {
    return documentsOf(this.saved, type);
  }
}

function feeScheduleOf(spec: ProposalParams): AnyFeeScheduleCategory[] {
  const feeSchedule = spec.workAgreement.feeSchedule;
  return Masked.isPresentNotMasked(feeSchedule) ? [...feeSchedule] : [];
}

function vendorTeamOf(spec: ProposalParams): VendorTeam | undefined {
  const vendorTeam = spec.workAgreement.vendorTeam;
  return vendorTeam instanceof VendorTeam ? vendorTeam : undefined;
}

function documentsOf(spec: ProposalParams, type: WorkDocumentType): WorkDocument[] {
  const documents = documentCollectionOf(spec, type);
  return Masked.isPresentNotMasked(documents) ? [...documents] : [];
}

function documentCollectionOf(spec: ProposalParams, type: WorkDocumentType) {
  if (type === WorkDocumentType.Conflicts) return spec.workAgreement.conflicts;
  if (type === WorkDocumentType.VendorPolicy) return spec.workAgreement.vendorPolicies;
  return spec.workAgreement.clientPolicies;
}

function sameId(a: { id: Guid }, b: { id: Guid }): boolean {
  return a.id.isEqualTo(b.id);
}

function samePerson(a: EntityMember, b: EntityMember): boolean {
  return a.isSamePersonAs(b);
}
